/**
 * The keybindings store's `persist.merge`: turns whatever `localStorage` holds
 * (an older or newer envelope, a hand-edited payload, a freshly imported
 * settings bundle) into valid store state.
 *
 * Hydration is one of the D6 validation points, so it applies
 * `validateKeymap`'s `overridesPatch` exactly like a mutation does — the
 * difference is that there is no mutation order (precedence: user override >
 * scheme > Jam default, then registry order) and no undo snapshot.
 */
import { deriveKeybindings, type DerivedKeybindings } from './deriveKeybindings';
import { normalizeOverrides, normalizeSchemeId } from './normalizeOverrides';
import type {
  KeybindingsData,
  KeybindingsSnapshot,
  KeybindingsState,
  KeybindingsStoreEnvironment,
} from './keybindingsStoreTypes';

export function dataOf(derived: DerivedKeybindings): Omit<KeybindingsData, 'undoSnapshot'> {
  return {
    scheme: derived.scheme,
    overrides: derived.overrides,
    keymap: derived.keymap,
    notices: derived.notices,
    unknownOverrideIds: derived.unknownOverrideIds,
  };
}

export type HydratedKeybindings = {
  readonly state: KeybindingsState;
  /**
   * True when the stored payload differs from its normalized, validated form.
   * `persist` only writes back after a version migration, so the store forces
   * one write in that case — otherwise a patch applied here (a reserved chord
   * removed, a displaced loser unbound) would live in memory only.
   */
  readonly writeBack: boolean;
};

const isPlainRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export function hydrateKeybindings(
  persisted: unknown,
  current: KeybindingsState,
  environment: KeybindingsStoreEnvironment,
): HydratedKeybindings {
  const raw = isPlainRecord(persisted) ? persisted : {};
  const scheme = normalizeSchemeId(raw.scheme, environment.schemes);
  const { overrides } = normalizeOverrides(raw.overrides);
  const derived = deriveKeybindings({ scheme, overrides }, environment, { previous: current });
  const normalized: KeybindingsSnapshot = { scheme, overrides: derived.overrides };
  return {
    state: { ...current, ...dataOf(derived), undoSnapshot: null },
    writeBack: persisted !== undefined && JSON.stringify(normalized) !== JSON.stringify(persisted),
  };
}
